import { useCallback } from 'react';
import { omit } from 'ramda';
import { useMutation, useApolloClient } from '@apollo/react-hooks';
import { UserRewardStatus } from '../../constants';
import { useCountry } from '../../components/CountrySelector';
import { useUser, USER_QUERY } from '../useUser';
import { GET_REWARDS_SUMMARY_QUERY } from '../useRewardsSummary';
import { UseUserQuery } from '../../generated/types/UseUserQuery';
import { ENROLL_MUTATION, EnrollMutation, EnrollMutationVariables } from './useEnrollMutation.rest';

export const useEnrollMutation = () => {
  const client = useApolloClient();
  const { country } = useCountry();

  const { user, setUserData } = useUser();
  const [mutate, { loading, error }] = useMutation<EnrollMutation, EnrollMutationVariables>(
    ENROLL_MUTATION,
    {
      refetchQueries: user.isLoggedIn
        ? [
            {
              query: GET_REWARDS_SUMMARY_QUERY,
              variables: {
                countryCode: country.alpha2Code
              }
            }
          ]
        : []
    }
  );

  const handleEnroll = useCallback(async () => {
    const {
      data: { user }
    } = await client.query<UseUserQuery>({ query: USER_QUERY });

    if (!user) return;

    if (user.id) {
      await mutate({
        variables: {
          id: user.id,
          input: {
            data: {
              type: 'users',
              attributes: {
                country_code: country.alpha2Code,
                enroll_to_loyalty: true
              },
              id: user.id
            }
          }
        }
      });
      setUserData({
        ...omit(['isLoggedIn', '__typename'], user),
        userLoyaltyStatus: UserRewardStatus.Enrolled
      });

      return Promise.resolve();
    }

    return Promise.reject('User id not found.');
  }, [client, country.alpha2Code, mutate, setUserData]);

  return { handleEnroll, loading, error };
};
